import {
    areShapesEquivalent,
    enumerateShapeItems,
    resolveFaceShapeByRef,
    resolveFaceShapesByEdge,
} from './common';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;

/**
 * 边支撑面解析选项。
 */
export interface ResolveEdgeSupportFaceOptions {
    /** SolidWorks 方向翻转对应的相邻面偏移。 */
    preferredFaceOffset?: 0 | 1;
}

/**
 * 判断候选面是否是当前边的相邻面。
 *
 * @param adjacentFaces 当前边的相邻面集合。
 * @param faceShape 候选面。
 * @returns 是否为相邻面。
 */
function isAdjacentFace(adjacentFaces: unknown[], faceShape: unknown): boolean {
    return adjacentFaces.some((candidate) => areShapesEquivalent(candidate, faceShape));
}

/**
 * 按运行时相邻面提示解析支撑面。
 *
 * 设计说明：
 * 1. 相邻面提示来自用户选择边线时的权威快照，比旧 face ref 更接近当前会话；
 * 2. 但 topologyIndex 仍不能单独当真，因此这里必须校验命中的面确实和当前边相邻；
 * 3. 若 hint 已经随上游重建失效，则返回 null，让后续 ref / 几何相邻面路径继续兜底。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 源 Shape。
 * @param selection 边选择项。
 * @param adjacentFaces 当前边相邻面集合。
 * @param preferredFaceOffset 优先相邻面偏移。
 * @returns 支撑面；无法稳定命中时返回 null。
 */
function resolveSupportFaceByRuntimeHint(
    puppy: PuppyKernel,
    shape: unknown,
    selection: SelectionItemRef,
    adjacentFaces: unknown[],
    preferredFaceOffset: 0 | 1,
): unknown | null {
    const faceHints = (selection.runtimeTopologyHint?.adjacentFaceHints ?? [])
        .filter((hint): hint is RuntimeTopologyFaceHint => Number.isInteger(hint.topologyIndex));
    const preferredHint = faceHints[preferredFaceOffset] ?? faceHints[0] ?? null;
    if (!preferredHint) {
        return null;
    }
    const faces = enumerateShapeItems(puppy, shape, 'face');
    const hintedFace = faces[preferredHint.topologyIndex];
    if (!hintedFace || !isAdjacentFace(adjacentFaces, hintedFace)) {
        return null;
    }
    return hintedFace;
}

/**
 * 按选择时记录的相邻面 ref 解析支撑面。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 源 Shape。
 * @param metadata 源 Shape 元数据。
 * @param selection 边选择项。
 * @param adjacentFaces 当前边相邻面集合。
 * @param preferredFaceOffset 优先相邻面偏移。
 * @returns 支撑面；ref 失效或不再相邻时返回 null。
 */
function resolveSupportFaceByAdjacentRefs(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    selection: SelectionItemRef,
    adjacentFaces: unknown[],
    preferredFaceOffset: 0 | 1,
): unknown | null {
    const preferredFaceRefs = (selection.adjacentFaceRefs ?? [])
        .filter((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0);
    const orderedRefs = [
        preferredFaceRefs[preferredFaceOffset],
        preferredFaceRefs[0],
        preferredFaceRefs[1],
    ].filter((candidate, index, refs): candidate is string => (
        typeof candidate === 'string'
        && candidate.length > 0
        && refs.indexOf(candidate) === index
    ));
    for (const preferredRef of orderedRefs) {
        try {
            const faceShape = resolveFaceShapeByRef(puppy, shape, metadata, preferredRef);
            if (isAdjacentFace(adjacentFaces, faceShape)) {
                return faceShape;
            }
        } catch {
            /**
             * 上游草图尺寸变化后，旧 face ref 可能已经无法在新 Shape 中解析。
             * 这不能中断倒角求解，必须继续尝试其它稳定线索和几何相邻面兜底。
             */
        }
    }
    return null;
}

/**
 * 解析边倒角使用的支撑面。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 源 Shape。
 * @param metadata 源 Shape 元数据。
 * @param selection 边选择项。
 * @param edgeShape 当前边 Shape。
 * @param options 解析选项。
 * @returns Puppy CAD 内核 支撑面 Shape。
 */
export function resolveEdgeSupportFace(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    selection: SelectionItemRef,
    edgeShape: unknown,
    options: ResolveEdgeSupportFaceOptions = {},
): unknown {
    const preferredFaceOffset = options.preferredFaceOffset ?? 0;
    const adjacentFaces = resolveFaceShapesByEdge(puppy, shape, edgeShape);
    const hintedFace = resolveSupportFaceByRuntimeHint(
        puppy,
        shape,
        selection,
        adjacentFaces,
        preferredFaceOffset,
    );
    if (hintedFace) {
        return hintedFace;
    }

    const refFace = resolveSupportFaceByAdjacentRefs(
        puppy,
        shape,
        metadata,
        selection,
        adjacentFaces,
        preferredFaceOffset,
    );
    if (refFace) {
        return refFace;
    }

    if (adjacentFaces.length > 0) {
        return adjacentFaces[preferredFaceOffset] ?? adjacentFaces[0]!;
    }

    throw new Error(`未找到边的支撑面，无法确定倒角方向: ${selection.persistentRef}`);
}
